// Persistent floating action buttons (Kit / Message / Call), bottom-right.
//
// Mounted ONCE at shell level (renderAppShell) so the buttons appear on every
// route — they live outside the router-controlled <main>, so navigation never
// tears them down. Previously this was a per-page dashboard widget
// (mountDashboardFab) and so only showed on the dashboard.
//
// • Kit     → opens the shell Kit assistant panel (openKit singleton).
// • Message → contacts popup → inline chat (openChatForContact, shared with the
//             dashboard messages widget).
// • Call    → contacts popup → DEFRAUD-SAFE masked dial via POST /portal/calls/dial
//             (NEVER tel: — the retailer's number stays server-side; mirrors the
//             shell mini-CRM onCall path). Returns 503 until Twilio Phase 3, so it
//             toasts a friendly "coming soon" rather than leaking a number.
//
// Message/Call carry `data-perm="messages"` so the shell role-gate hides them
// for roles without the messages capability. Kit is available to all roles.

import type { PageContext } from "./types";
import { iconMarkup } from "@tkos/shared-ui";
import { openKit } from "./kit-panel";
import { fetchActiveAlerts } from "./kit-alerts";
import * as realtime from "./realtime";
import { openChatForContact } from "../pages/dashboard/messages-widget";
import "./fab.css";

interface Contact { id: string; name: string; phone: string; email: string; type: string }

const PALETTE = ["#e58200", "#3b82f6", "#a855f7", "#22c55e", "#0ea5e9", "#ef4444", "#f59e0b"];
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}
function initials(n: string): string {
  return (n.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "?";
}
function colorFor(s: string): string {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

export function mountShellFab(ctx: PageContext): () => void {
  let cache: Contact[] | null = null;
  let pop: HTMLElement | null = null;

  const fab = document.createElement("div");
  fab.className = "tkos-fab";
  fab.innerHTML = `
    <button type="button" class="tkos-fab__btn tkos-fab__btn--kit" data-fab="kit" aria-label="Ask Kit" title="Ask Kit">${iconMarkup("sparkle", { size: "md" })}</button>
    <button type="button" class="tkos-fab__btn tkos-fab__btn--message" data-perm="messages" data-fab="message" aria-label="Message a contact" title="Message">${iconMarkup("chat", { size: "md" })}</button>
    <button type="button" class="tkos-fab__btn tkos-fab__btn--call" data-perm="messages" data-fab="call" aria-label="Call a contact" title="Call">${iconMarkup("phone", { size: "md" })}</button>`;
  document.body.appendChild(fab);

  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") closePop(); };
  function closePop(): void {
    if (pop) { pop.remove(); pop = null; }
    document.removeEventListener("keydown", onKey);
  }

  const loadContacts = async (): Promise<Contact[]> => {
    if (cache) return cache;
    const out: Contact[] = [];
    const retailerId = ctx.user?.retailerId;
    if (!retailerId) { return out; } // not authenticated yet — don't cache the empty result
    try {
      const resp = await ctx.api<{ contacts?: Contact[] }>(`/portal/contacts?retailerId=${encodeURIComponent(retailerId)}`);
      for (const c of resp.contacts ?? []) {
        out.push({ id: String(c.id ?? ""), name: String(c.name ?? "Unnamed"), phone: String(c.phone ?? ""), email: String(c.email ?? ""), type: String(c.type ?? "customer") });
      }
    } catch { /* leave empty */ }
    cache = out;
    return out;
  };

  // DEFRAUD-SAFE dial: the masked endpoint keeps the retailer's number
  // server-side. Mirrors shell.ts installMiniCrmDelegation onCall. NEVER tel:.
  const dialContact = async (id: string): Promise<void> => {
    try {
      await ctx.api<{ ok: boolean }>("/portal/calls/dial", {
        method: "POST",
        body: JSON.stringify({ customerId: id }),
      });
      ctx.showToast("Connecting call…", "success");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 503) {
        ctx.showToast("Click-to-call goes live with Twilio (Phase 3).", "info");
      } else {
        ctx.showToast("Could not start call", "error");
      }
    }
  };

  const rowHtml = (c: Contact, mode: string): string => {
    const sub = mode === "call" ? (c.phone || "No phone on file") : (c.email || "No email on file");
    return `
      <button type="button" class="tkos-fab-row" data-id="${esc(c.id)}" data-name="${esc(c.name)}" data-phone="${esc(c.phone)}" data-email="${esc(c.email)}">
        <span class="tkos-fab-row__av" style="background:${colorFor(c.name)}">${esc(initials(c.name))}</span>
        <span class="tkos-fab-row__tx"><span class="tkos-fab-row__nm">${esc(c.name)}</span><span class="tkos-fab-row__sub">${esc(sub)}</span></span>
      </button>`;
  };

  const openContacts = async (mode: "call" | "message"): Promise<void> => {
    closePop();
    pop = document.createElement("div");
    pop.className = "tkos-fab-pop-overlay";
    pop.innerHTML = `
      <div class="tkos-fab-pop" role="dialog" aria-modal="true" aria-label="${mode === "call" ? "Call a contact" : "Message a contact"}">
        <div class="tkos-fab-pop__head">
          <span class="tkos-fab-pop__title">${mode === "call" ? "Call a contact" : "Message a contact"}</span>
          <button type="button" class="tkos-fab-pop__x" data-role="close" aria-label="Close">&times;</button>
        </div>
        <input type="search" class="tkos-fab-pop__search" data-role="search" placeholder="Search customers, trade &amp; suppliers…" />
        <div class="tkos-fab-pop__list" data-role="list"><div class="tkos-fab-pop__loading">Loading…</div></div>
      </div>`;
    pop.addEventListener("mousedown", (e) => { if (e.target === pop) closePop(); });
    pop.querySelector<HTMLButtonElement>('[data-role="close"]')!.addEventListener("click", closePop);
    document.addEventListener("keydown", onKey);
    document.body.appendChild(pop);

    const listEl = pop.querySelector<HTMLElement>('[data-role="list"]')!;
    const all = await loadContacts();
    if (!pop) return;

    const paint = (q: string): void => {
      const needle = q.trim().toLowerCase();
      const match = (c: Contact): boolean => !needle || c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle) || c.phone.toLowerCase().includes(needle);
      const customers = all.filter((c) => c.type === "customer" && match(c));
      const trade     = all.filter((c) => c.type === "trade" && match(c));
      // Suppliers = anything that isn't a customer or trade contact, so an
      // unexpected future type still surfaces rather than silently vanishing.
      const suppliers = all.filter((c) => c.type !== "customer" && c.type !== "trade" && match(c));
      const section = (title: string, rows: Contact[]): string =>
        rows.length === 0 ? "" : `<div class="tkos-fab-pop__sec">${esc(title)}</div>${rows.map((c) => rowHtml(c, mode)).join("")}`;
      const body = section("Customers", customers) + section("Trade", trade) + section("Suppliers", suppliers);
      listEl.innerHTML = body || `<div class="tkos-fab-pop__empty">No contacts found.</div>`;
      listEl.querySelectorAll<HTMLButtonElement>(".tkos-fab-row").forEach((row) => {
        row.addEventListener("click", () => {
          const id = row.dataset["id"] || "";
          const name = row.dataset["name"] || "";
          const email = row.dataset["email"] || "";
          if (mode === "call") {
            closePop();
            void dialContact(id);
          } else {
            closePop();
            void openChatForContact(ctx, ctx.user?.retailerId ?? "", { name, email });
          }
        });
      });
    };
    paint("");
    const search = pop.querySelector<HTMLInputElement>('[data-role="search"]')!;
    search.addEventListener("input", () => paint(search.value));
    search.focus();
  };

  // ── Collapsible cluster on mobile ──────────────────────────────────────
  // On phones the stack collapses to a single Kit button to keep the bottom
  // corner clear. First tap on Kit expands Message + Call; a second tap on
  // Kit opens the assistant. Picking Message/Call (or tapping away) collapses
  // the cluster again. On desktop all three are always visible.
  const mqMobile = window.matchMedia("(max-width: 768px)");
  const syncCollapsed = (): void => {
    fab.classList.toggle("is-collapsible", mqMobile.matches);
    if (!mqMobile.matches) fab.classList.remove("is-expanded");
  };
  syncCollapsed();
  mqMobile.addEventListener("change", syncCollapsed);
  const collapse = (): void => fab.classList.remove("is-expanded");
  const onDocPointer = (e: Event): void => {
    if (!fab.classList.contains("is-expanded")) return;
    if (!fab.contains(e.target as Node)) collapse();
  };
  document.addEventListener("pointerdown", onDocPointer);

  fab.querySelectorAll<HTMLButtonElement>("[data-fab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const which = btn.dataset["fab"];
      if (which === "kit") {
        // Collapsed on mobile → first tap reveals the cluster instead of
        // opening Kit.
        if (mqMobile.matches && !fab.classList.contains("is-expanded")) {
          fab.classList.add("is-expanded");
          return;
        }
        collapse();
        openKit();
        return;
      }
      collapse();
      void openContacts(which === "call" ? "call" : "message");
    });
  });

  // Kit FAB pulses while Kit has proactive alerts to show. The `kit.alert`
  // realtime event fires whenever the alert stack changes (raised or dismissed),
  // so we re-check then; also check once on mount. Same source the dashboard
  // kit-bar uses.
  const kitBtn = fab.querySelector<HTMLButtonElement>(".tkos-fab__btn--kit");
  let disposed = false;
  const refreshAlerts = async (): Promise<void> => {
    const rid = ctx.user?.retailerId;
    if (!rid || !kitBtn) return;
    const alerts = await fetchActiveAlerts(rid);
    if (disposed || !kitBtn) return;
    kitBtn.classList.toggle("has-alerts", alerts.length > 0);
  };
  void refreshAlerts();
  const offKitAlert = realtime.onEvent("kit.alert", () => { void refreshAlerts(); });

  return () => {
    disposed = true;
    offKitAlert();
    mqMobile.removeEventListener("change", syncCollapsed);
    document.removeEventListener("pointerdown", onDocPointer);
    closePop();
    fab.remove();
  };
}
